import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { PageLoader } from "./components/PageLoader";
import { Suspense, lazy, useCallback, useEffect, useState } from "react";

import { AdminRoute } from "./components/AdminRoute";
import AccountSetup from "./pages/AccountSetup";
import { AuthRedirect } from "./components/AuthRedirect";
import { ClientRoute } from "./components/ClientRoute";
import InitCheck from "./components/InitCheck";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import PasswordReset from "./pages/PasswordReset";
import ProtectedRoute from "./components/ProtectedRoute";
import Signup from "./pages/Signup";
import { TeamRoute } from "./components/TeamRoute";
import Unauthorized from "./pages/Unauthorized";
import { useAuthStore } from "./stores/authStore";
import TeamHome from "./pages/TeamHome";
import ClientProjects from "./pages/ClientProjects";
import Settings from "./pages/Settings";
import Profile from "./pages/Profile";
import ViewerRoute from "./pages/ViewerRoute";

// Lazy loading des pages lourdes
const AdminPage = lazy(() => import("./pages/Admin"));

function App() {
  const [needsInitialization, setNeedsInitialization] = useState<
    boolean | null
  >(null);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const checkAuth = useAuthStore((state) => state.checkAuth);

  const handleInitStatusLoaded = useCallback((needs: boolean) => {
    setNeedsInitialization(needs);
  }, []);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (needsInitialization && isAuthenticated) {
      // L'utilisateur vient de s'inscrire : l'initialisation est terminée
      setNeedsInitialization(false);
    }
  }, [isAuthenticated, needsInitialization]);

  useEffect(() => {
    if (user) {
      console.log(`[App] rôle de l'utilisateur connecté : ${user.role}`);
    } else {
      console.log("[App] aucun utilisateur connecté");
    }
  }, [user]);

  if (needsInitialization === null) {
    return <InitCheck onInitStatusLoaded={handleInitStatusLoaded} />;
  }

  return (
    <Router>
      <Routes>
        {/* Si initialisation nécessaire, afficher la page de signup */}
        {needsInitialization && !isAuthenticated ? (
          <>
            <Route path="/signup" element={<Signup />} />
            <Route path="*" element={<Navigate to="/signup" replace />} />
          </>
        ) : (
          <>
            {/* Sinon, afficher les routes normales */}
            <Route
              path="/login"
              element={
                <AuthRedirect>
                  <Login />
                </AuthRedirect>
              }
            />
            <Route
              path="/signup"
              element={
                <AuthRedirect>
                  <Signup />
                </AuthRedirect>
              }
            />
            <Route
              path="/account-setup"
              element={
                <AuthRedirect>
                  <AccountSetup />
                </AuthRedirect>
              }
            />
            <Route
              path="/password-reset"
              element={
                <AuthRedirect>
                  <PasswordReset />
                </AuthRedirect>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AdminPage />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/unauthorized"
              element={
                <ProtectedRoute>
                  <Unauthorized />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team"
              element={
                <TeamRoute>
                  <TeamHome />
                </TeamRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ClientRoute>
                  <ClientProjects />
                </ClientRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ViewerRoute />
                </ProtectedRoute>
              }
            />
            {/* Route catch-all pour les pages inexistantes */}
            <Route path="*" element={<NotFound />} />
          </>
        )}
      </Routes>
    </Router>
  );
}

export default App;
